import { Controller } from "@hotwired/stimulus"
import { yieldDrawerEscape } from "../lib/drawer_escape"

// FD-330: the error-group peek drawer. The generic `peek` controller is hardcoded to the
// work-item #peek_panel frame; this is the parallel for the distinct
// #error_group_peek_panel frame. Error list rows are plain advancing links targeting that
// frame, which the server renders cheaply (ErrorGroupPeekFrame). We open the drawer when
// the frame gains content and close it when the frame empties: the close link navigates
// the frame to the no-peek URL, which renders empty.
//
// FD-1311: the drawer is a <cs-drawer>, a modal <dialog>. It handles Escape and backdrop
// clicks itself and tells us with cs-hide; a close the user asked for re-fires the close
// link, so the URL and the frame follow. Ours (the frame emptied) needs nothing more.
const FRAME_ID = "error_group_peek_panel"

export default class extends Controller {
  static targets = ["panel"]

  connect() {
    this._open = false // whether WE want it open: tells the user's close from ours
    // The drawer is playing its close animation. It can't be re-opened (or closed again)
    // in that window, so a re-open waits for cs-after-hide. See peek_controller#openDrawer.
    this._closing = false
    this._reopen = false
    this._onFrameLoad = this.onFrameLoad.bind(this)
    this._onHide = this.onHide.bind(this)
    this._onAfterHide = this.onAfterHide.bind(this)
    document.addEventListener("turbo:frame-load", this._onFrameLoad)
    this.element.addEventListener("cs-hide", this._onHide)
    this.element.addEventListener("cs-after-hide", this._onAfterHide)
    // A deep link arrives with the panel populated (and the drawer server-rendered open).
    this.hasContent() ? this.reveal() : this.hide()
  }

  disconnect() {
    document.removeEventListener("turbo:frame-load", this._onFrameLoad)
    this.element.removeEventListener("cs-hide", this._onHide)
    this.element.removeEventListener("cs-after-hide", this._onAfterHide)
    this.releaseEscape()
  }

  onFrameLoad(event) {
    if (event.target?.id !== FRAME_ID) return
    this.hasContent() ? this.reveal() : this.hide()
  }

  // The drawer is closing. If we still wanted it open, the user pressed Escape or clicked
  // the backdrop: fire the close link (an advancing frame nav to the no-peek URL, which
  // empties the frame; onFrameLoad then finds the drawer already closed).
  onHide(event) {
    if (event.target !== this.element) return // a cs-* element inside the panel closing
    this._closing = true
    if (!this._open) return
    this._open = false
    this.releaseEscape()
    this.element.querySelector("[data-error-peek-close]")?.click()
  }

  onAfterHide(event) {
    if (event.target !== this.element) return
    this._closing = false
    if (this._reopen) {
      this._reopen = false
      this.element.toggleAttribute("open", true)
    }
  }

  reveal() {
    this.syncLabel()
    if (this._open) return
    this._open = true
    this._releaseEscape = yieldDrawerEscape()
    if (this._closing && this.element.hasAttribute("open")) this._reopen = true
    else this.element.toggleAttribute("open", true)
  }

  hide() {
    this._open = false
    this._reopen = false
    this.releaseEscape()
    if (!this._closing) this.element.toggleAttribute("open", false)
  }

  releaseEscape() {
    this._releaseEscape?.()
    this._releaseEscape = null
  }

  // The drawer's accessible name follows the group on screen.
  syncLabel() {
    const label = this.element.querySelector("[data-peek-body]")?.getAttribute("data-peek-label")
    if (label) this.element.setAttribute("label", label)
  }

  hasContent() {
    const frame = this.element.querySelector(`turbo-frame#${FRAME_ID}`)
    return !!frame && frame.children.length > 0
  }
}
